import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import api from '../../../utils/api';
import { useToast } from '../../../components/ui/ToastProvider';
import { useAuth } from '../../authentication/hooks/useAuth';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { parseEntitlements } from '../../pro/entitlements';
import { fetchProfile, getCachedProfile, patchCachedProfile, setCachedProfile } from '../../pro/profileCache';
import {
  fetchAccountHistory,
  getCachedAccountHistory,
  invalidateCachedAccountHistory,
} from '../utils/accountHubCache';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import {
  PLAN_CTA,
  billingFlagFromSearch,
  formatPlanRenewsAt,
  planBlurb,
  planDisplayName,
} from '../../pro/planCopy';
import {
  captureTagOutCodeFromSearch,
  clearPendingTagOutCode,
  normalizeTagOutCode,
  peekPendingTagOutCode,
  tagOutRedeemError,
} from '../../billing/tagOutCode';
import { ProfilePanel } from './ProfilePanel';
import '../profile-design.css';

function formatMoney(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

function redirectToStripe(url) {
  window.location.assign(url);
}

export function AccountSection() {
  const { logout } = useAuth();
  const { currentUser } = useCurrentUser();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { show: showToast } = useToast();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const [googleLinked, setGoogleLinked] = useState(false);
  const [accountEmail, setAccountEmail] = useState('');
  const [accountUsername, setAccountUsername] = useState('');
  const [accountHistory, setAccountHistory] = useState(() => getCachedAccountHistory() || []);
  const [accountHistoryLoading, setAccountHistoryLoading] = useState(() => !getCachedAccountHistory());
  const [plan, setPlan] = useState('free');
  const [planRenewsAt, setPlanRenewsAt] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [tagOutCode, setTagOutCode] = useState('');
  const [tagOutBusy, setTagOutBusy] = useState(false);
  const [tagOutError, setTagOutError] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const billingHandled = useRef(null);

  const applyProfile = useCallback((data) => {
    const row = data || {};
    setCachedProfile(row);
    setGoogleLinked(Boolean(row.google_linked));
    setAccountEmail((row.email || '').trim());
    setAccountUsername((row.username || row.handle || '').trim().replace(/^@+/, ''));
    setPlan(parseEntitlements(row).plan);
    setPlanRenewsAt(row.plan_renews_at || null);
  }, []);

  const loadProfile = useCallback(async ({ force = false } = {}) => {
    const res = await fetchProfile(api, { force });
    applyProfile(res?.data || {});
  }, [applyProfile]);

  useEffect(() => {
    const cached = getCachedProfile({ allowStale: true });
    if (cached) applyProfile(cached);
    loadProfile().catch(() => {});
  }, [applyProfile, loadProfile]);

  useEffect(() => {
    const captured = captureTagOutCodeFromSearch(search);
    const pending = peekPendingTagOutCode() || captured;
    if (pending) setTagOutCode(pending);
    if (!captured) return undefined;
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    params.delete('code');
    const next = params.toString();
    navigate(
      { pathname: pathname || '/profile/account', search: next ? `?${next}` : '' },
      { replace: true }
    );
    return undefined;
  }, [navigate, pathname, search]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const cached = getCachedAccountHistory();
      if (cached) {
        setAccountHistory(cached);
        setAccountHistoryLoading(false);
        return;
      }
      setAccountHistoryLoading(true);
      try {
        const res = await fetchAccountHistory(api);
        if (!alive) return;
        const results = res?.data?.results ?? [];
        setAccountHistory(Array.isArray(results) ? results : []);
      } catch (e) {
        if (!alive) return;
        setAccountHistory([]);
      } finally {
        if (alive) setAccountHistoryLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const flag = billingFlagFromSearch(search);
    if (flag !== 'success' && flag !== 'cancel') return undefined;
    if (billingHandled.current === flag) return undefined;
    billingHandled.current = flag;
    let cancelled = false;
    (async () => {
      if (flag === 'success') {
        try {
          const synced = await api.post('/billing/sync/');
          if (!cancelled) applyProfile(synced?.data || {});
          invalidateCachedAccountHistory();
          await loadProfile({ force: true });
          const hist = await fetchAccountHistory(api, { force: true });
          if (!cancelled) {
            setAccountHistory(hist?.data?.results ?? []);
            showToast({ title: 'You are on Yona Pro', variant: 'success' });
          }
          clearPendingTagOutCode();
        } catch (e) {
          if (!cancelled) {
            setError('Could not refresh your plan. Try again in a moment.');
          }
        }
      } else if (!cancelled) {
        showToast({ title: 'Checkout canceled', variant: 'info' });
      }
      if (!cancelled) {
        navigate({ pathname: pathname || '/profile/account', search: '' }, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyProfile, loadProfile, navigate, pathname, search, showToast]);

  const startCheckout = async () => {
    setError('');
    setCheckoutBusy(true);
    try {
      const resp = await api.post('/billing/checkout/');
      const data = resp?.data || {};
      if (data.url) {
        redirectToStripe(data.url);
        return;
      }
      if (data.already_subscribed) {
        await loadProfile({ force: true });
        return;
      }
      setError('Could not start checkout.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not start checkout.');
    } finally {
      setCheckoutBusy(false);
    }
  };

  const applyTagOutCode = async (event) => {
    event?.preventDefault();
    const code = normalizeTagOutCode(tagOutCode);
    setTagOutError('');
    if (!code) {
      setTagOutError('Enter the code from Tag Out.');
      return;
    }
    setTagOutBusy(true);
    try {
      const resp = await api.post('/tag-out/redeem/', { code });
      const data = resp?.data || {};
      if (data.url) {
        clearPendingTagOutCode();
        redirectToStripe(data.url);
        return;
      }
      setTagOutError('Could not apply that Tag Out code.');
    } catch (e) {
      setTagOutError(tagOutRedeemError(e));
    } finally {
      setTagOutBusy(false);
    }
  };

  const linkGoogleAccount = async () => {
    setError('');
    setSuccess('');
    try {
      setLinkingGoogle(true);
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        setError('Google script not loaded');
        setLinkingGoogle(false);
        return;
      }
      await new Promise((resolve, reject) => {
        try {
          const client = window.google.accounts.oauth2.initCodeClient({
            client_id: process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID,
            scope: 'openid email profile',
            ux_mode: 'popup',
            callback: async (response) => {
              try {
                if (!response || !response.code) {
                  setError('Linking cancelled');
                  return resolve();
                }
                const resp = await api.post('/auth/google/link/code/', {
                  code: response.code,
                  redirect_uri: 'postmessage',
                });
                if (resp?.data?.status === 'linked') {
                  setSuccess('Google account linked');
                  setGoogleLinked(true);
                  patchCachedProfile({ google_linked: true });
                } else {
                  setError('Linking failed');
                }
              } catch (e) {
                setError(e?.response?.data?.error || 'Linking failed');
              } finally {
                resolve();
              }
            },
          });
          client.requestCode();
        } catch (e) {
          reject(e);
        }
      });
    } catch (e) {
      setError('Linking failed');
    } finally {
      setLinkingGoogle(false);
    }
  };

  const startPortal = async () => {
    setError('');
    setCheckoutBusy(true);
    try {
      const resp = await api.post('/billing/portal/');
      const url = resp?.data?.url;
      if (url) {
        redirectToStripe(url);
        return;
      }
      setError('Could not open billing.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not open billing.');
    } finally {
      setCheckoutBusy(false);
    }
  };

  const signedInAs =
    accountEmail ||
    (accountUsername ? `@${accountUsername}` : '') ||
    (currentUser?.email || '').trim() ||
    (currentUser?.username && currentUser.username !== 'Unknown'
      ? `@${currentUser.username}`
      : '') ||
    'your Yona account';

  const addOnsTotal = accountHistory.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

  const handleLogout = () => {
    try {
      logout();
    } catch (e) {
      setError('Could not log out. Please try again.');
    }
  };

  const handleDeleteAccount = async () => {
    setError('');
    setDeleting(true);
    try {
      await api.post('/account/delete/', { confirm: true });
      setShowDelete(false);
      logout();
    } catch (e) {
      setError(
        e?.response?.data?.detail || 'Could not delete the account. Try again or email hello@yona.land.'
      );
      setShowDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const planName = planDisplayName(plan);
  const planCopy = planBlurb(plan);

  return (
    <div className="y-profile-account-grid" data-testid="account-section">
      <div className="y-profile-account-grid__main">
        <ProfilePanel title="CURRENT PLAN" variant="loden">
          <div className="y-account-plan">
            <div className="y-account-plan__copy">
              <div className="y-account-plan__title-row">
                <h2 className="y-account-plan__name" data-testid="account-plan-name">{planName}</h2>
                <span className="y-account-plan__badge">ACTIVE</span>
              </div>
              <p className="y-account-plan__blurb">{planCopy}</p>
            </div>
            {plan === 'free' ? (
              <button
                type="button"
                className="y-account-plan__change"
                disabled={checkoutBusy}
                onClick={startCheckout}
                data-testid="account-upgrade"
              >
                {checkoutBusy ? 'STARTING…' : PLAN_CTA.upgrade.toUpperCase()}
              </button>
            ) : (
              <button
                type="button"
                className="y-account-plan__change"
                disabled={checkoutBusy}
                onClick={startPortal}
                data-testid="account-manage-billing"
              >
                {checkoutBusy ? 'OPENING…' : PLAN_CTA.manage.toUpperCase()}
              </button>
            )}
          </div>
          <form className="y-account-tagout" onSubmit={applyTagOutCode} data-testid="account-tag-out">
            <label className="y-account-tagout__label" htmlFor="tag-out-code">
              Tag Out code
            </label>
            <div className="y-account-tagout__row">
              <input
                id="tag-out-code"
                className="y-account-tagout__input"
                value={tagOutCode}
                onChange={(e) => setTagOutCode(e.target.value)}
                placeholder="TAG3-XXXXXXXX"
                autoComplete="off"
                spellCheck="false"
                data-testid="account-tag-out-code"
              />
              <button
                type="submit"
                className="y-account-plan__change"
                disabled={tagOutBusy}
                data-testid="account-tag-out-apply"
              >
                {tagOutBusy ? 'APPLYING…' : 'APPLY'}
              </button>
            </div>
            <p className="y-account-tagout__hint">
              Won on yona.land. Apply opens checkout — card for three free months, or $20 for the year.
            </p>
            {tagOutError ? (
              <div className="y-profile-msg y-profile-msg--error" data-testid="account-tag-out-error">
                {tagOutError}
              </div>
            ) : null}
          </form>
        </ProfilePanel>

        <ProfilePanel title="ACCOUNT & SIGN-IN">
          <div className="y-account-signin">
            <p className="y-account-signin__text">
              Signed in as <strong>{signedInAs}</strong>
            </p>
            <div className="y-account-signin__actions">
              <button
                type="button"
                className={`y-account-btn y-account-btn--ghost${googleLinked ? ' is-linked' : ''}`}
                disabled={linkingGoogle || googleLinked}
                onClick={linkGoogleAccount}
              >
                {googleLinked ? 'GOOGLE LINKED' : linkingGoogle ? 'LINKING…' : 'LINK GOOGLE'}
              </button>
              <button
                type="button"
                className="y-account-btn y-account-btn--danger"
                onClick={handleLogout}
                data-testid="account-logout"
              >
                LOG OUT
              </button>
            </div>
            <button
              type="button"
              className="y-account-delete"
              onClick={() => setShowDelete(true)}
              disabled={deleting}
              data-testid="account-delete"
            >
              {deleting ? 'DELETING…' : 'Delete account'}
            </button>
          </div>
          {error ? <div className="y-profile-msg y-profile-msg--error">{error}</div> : null}
          {success ? <div className="y-profile-msg y-profile-msg--ok">{success}</div> : null}
        </ProfilePanel>

        <ProfilePanel title="BILLING HISTORY" bodyClassName="y-profile-panel__body--flush-list">
          {accountHistoryLoading ? (
            <p className="y-account-empty">Loading…</p>
          ) : accountHistory.length === 0 ? (
            <p className="y-account-empty">
              No activity yet. Map prints and other add-ons will appear here.
            </p>
          ) : (
            accountHistory.map((row) => (
              <div key={row.id} className="y-profile-history-row">
                <div className="y-account-history__copy">
                  <p className="y-account-history__desc">{row.description || row.label}</p>
                  <p className="y-account-history__meta">
                    {row.occurred_at
                      ? new Date(row.occurred_at).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '—'}
                    {row.label ? ` · ${String(row.label).toUpperCase()}` : ''}
                  </p>
                </div>
                <div className="y-account-history__right">
                  <span className="y-account-paid-badge">
                    <FaCheck size={9} aria-hidden="true" />
                    PAID
                  </span>
                  {row.amount != null ? (
                    <span className="y-account-history__amount">{formatMoney(row.amount)}</span>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </ProfilePanel>
      </div>

      <div className="y-profile-account-grid__side">
        <ProfilePanel title="BILLING SUMMARY" bodyClassName="y-profile-panel__body--flush-list">
          <div className="y-profile-loadout-row">
            <span className="y-profile-loadout-row__key">Next charge</span>
            <span className="y-profile-loadout-row__val" data-testid="account-next-charge">
              {formatPlanRenewsAt(planRenewsAt)}
            </span>
          </div>
          <div className="y-profile-loadout-row">
            <span className="y-profile-loadout-row__key">Add-ons this year</span>
            <span className="y-profile-loadout-row__val">
              {addOnsTotal > 0 ? formatMoney(addOnsTotal) : '—'}
            </span>
          </div>
          <div className="y-profile-loadout-row">
            <span className="y-profile-loadout-row__key">Plan</span>
            <span className="y-profile-loadout-row__val" data-testid="account-plan-summary">{planName}</span>
          </div>
        </ProfilePanel>
      </div>
      <ConfirmModal
        show={showDelete}
        onCancel={() => !deleting && setShowDelete(false)}
        onConfirm={handleDeleteAccount}
        title="Delete your account?"
        body="This removes your account, personal ground, hunts, and messages. Clubs that still have other members stay; if you are president, that role moves to another member. Clubs with nobody else are deleted. Yona Pro billing stops. This cannot be undone."
        confirmLabel={deleting ? 'Deleting…' : 'Delete account'}
        cancelLabel="Keep account"
        confirmTestId="account-delete-confirm"
      />
    </div>
  );
}
